"use client";

import { c as _c } from "react/compiler-runtime";
import { matchesAnyPath } from "@/lib/utils/path-matches";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { getCrestaEmbedTargetId, subscribeToCrestaEmbedTarget } from "./embedTarget";
export type CrestaChatConfig = {
  enabled: boolean;
  scriptSrc: string;
  attributes?: Record<string, string>;
  styles?: Record<string, string>;
};

// The Cresta widget script only needs to load once per session. Once loaded it
// stays initialized, so we never re-append it.
let scriptLoaded = false;
export function resetInitialized() {
  scriptLoaded = false;
}

// The Cresta script renders its OWN launcher node — currently
// `<div class="cresta-widget-host">` — directly on <body>, as a sibling of the
// <df-messenger> element we create. That node outlives <df-messenger>, so
// removing our element does NOT hide the widget. The only reliable way to hide
// it on non-matching pages (and to react to SPA navigation) is a persistent
// stylesheet that hides both nodes.
//
// The `[class*="cresta-widget"]` matcher is intentionally broader than the exact
// `cresta-widget-host` class so a future Cresta rename (e.g. `cresta-widget-root`)
// keeps working, while staying namespaced enough to avoid hiding app elements.
const GATE_STYLE_ID = "cresta-chat-visibility";
const HIDE_WIDGET_CSS = 'df-messenger, [class*="cresta-widget"] { display: none !important; }';

// Contentful studio renders the site inside an iframe with `expEditorMode=true`
// on the URL. The layout's referer-based `isEditorMode` check doesn't reliably
// see the studio, so we detect it client-side from the live URL and never show
// the widget while editing.
const isStudioEditorMode = () => globalThis.window !== undefined && new URLSearchParams(globalThis.location.search).get("expEditorMode") === "true";

// Setting `target-id-in-embedded-mode` to any non-empty value switches the
// widget from a floating window to rendering inside that element; there is no
// separate on/off flag. Embedded mode renders no launcher, so without
// `auto-start` nothing would ever open the chat.
const EMBED_TARGET_ATTR = "target-id-in-embedded-mode";
const AUTO_START_ATTR = "auto-start";

/**
 * Client-side gating: this component is mounted globally in the root layout so
 * it can react to SPA navigation. The server only decides the global on/off
 * switch (`config.enabled`); which pages actually show the widget is decided
 * here from the live pathname.
 *
 * `paths` is an optional allowlist. When it is missing or empty, the widget
 * shows on every page. On non-matching pages the widget is hidden via CSS
 * rather than removed, because the Cresta script renders a `.cresta-widget-host`
 * node on <body> that we do not own and cannot reliably tear down.
 */
const CrestaChat = t0 => {
  const $ = _c(16);
  const {
    config,
    paths
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== pathname || $[1] !== paths) {
    t1 = !Array.isArray(paths) || paths.length === 0 || matchesAnyPath(paths, pathname);
    $[0] = pathname;
    $[1] = paths;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const pathAllowed = t1;
  const shouldShow = Boolean(config.enabled) && pathAllowed;
  let t2;
  if ($[3] !== config.attributes || $[4] !== config.enabled || $[5] !== config.scriptSrc || $[6] !== config.styles || $[7] !== shouldShow) {
    t2 = () => {
      if (!config.enabled) {
        return;
      }
      if (isStudioEditorMode()) {
        return;
      }
      const existingGate = document.getElementById(GATE_STYLE_ID);
      if (!shouldShow) {
        const gate = existingGate ?? document.createElement("style");
        if (!existingGate) {
          gate.id = GATE_STYLE_ID;
          document.head.appendChild(gate);
        }
        gate.textContent = HIDE_WIDGET_CSS;
        return;
      }
      existingGate?.remove();
      if (document.querySelector("df-messenger")) {
        return;
      }
      if (config.styles && Object.keys(config.styles).length > 0) {
        const styleEl = document.createElement("style");
        const cssVars = Object.entries(config.styles).map(_temp).join("\n");
        styleEl.textContent = `df-messenger {\n${cssVars}\n}`;
        document.head.appendChild(styleEl);
      }
      const dfMessenger = document.createElement("df-messenger");
      if (config.attributes) {
        for (const [attr, value_0] of Object.entries(config.attributes)) {
          dfMessenger.setAttribute(attr, value_0);
        }
      }
      const embedTargetId = getCrestaEmbedTargetId();
      if (embedTargetId) {
        dfMessenger.setAttribute(EMBED_TARGET_ATTR, embedTargetId);
        if (!config.attributes?.[AUTO_START_ATTR]) {
          dfMessenger.setAttribute(AUTO_START_ATTR, "true");
        }
      }
      document.body.appendChild(dfMessenger);
      if (!scriptLoaded) {
        const script = document.createElement("script");
        script.type = "module";
        script.src = config.scriptSrc;
        document.body.appendChild(script);
        scriptLoaded = true;
      }
    };
    $[3] = config.attributes;
    $[4] = config.enabled;
    $[5] = config.scriptSrc;
    $[6] = config.styles;
    $[7] = shouldShow;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== config || $[10] !== shouldShow) {
    t3 = [shouldShow, config];
    $[9] = config;
    $[10] = shouldShow;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  useEffect(t2, t3);
  let t4;
  if ($[12] !== config.attributes) {
    t4 = () => subscribeToCrestaEmbedTarget(() => {
      const dfMessenger_0 = document.querySelector("df-messenger");
      if (!dfMessenger_0) {
        return;
      }
      const embedTargetId_0 = getCrestaEmbedTargetId();
      if (!embedTargetId_0) {
        dfMessenger_0.removeAttribute(EMBED_TARGET_ATTR);
        return;
      }
      dfMessenger_0.setAttribute(EMBED_TARGET_ATTR, embedTargetId_0);
      if (!config.attributes?.[AUTO_START_ATTR]) {
        dfMessenger_0.setAttribute(AUTO_START_ATTR, "true");
      }
      console.warn(`[CrestaChat] Embed target "${embedTargetId_0}" registered after <df-messenger> was created, so the widget may have already initialized in floating mode. The attribute was applied late as a fallback.`);
    });
    $[12] = config.attributes;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== config) {
    t5 = [config];
    $[14] = config;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  useEffect(t4, t5);
  return null;
};
export default CrestaChat;
function _temp(t0) {
  const [prop, value] = t0;
  return `  ${prop}: ${value};`;
}